import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FormDrawerWrapper from '../../../../features/components/FormDrawerWrapper'
import EditSectionsList from '../../../../features/components/Forms/EditForm/EditSectionsList'
import NameSectionForm from './Sections/NameSection/NameSectionForm'
import NameSectionPreview from './Sections/NameSection/NameSectionPreview'
import BudgetSection from './Sections/BudgetSection'
import TikTokCampaignBudgetSectionPreview from '../../../ReusableFormSections/reusableBudgetSections/TikTokBudgetSections/TikTokCampaignBudgetSection/TikTokCampaignBudgetSectionPreview'

import useManageEditFormData from '../../../../hooks/formHooks/useManageEditFormData'
import { insertIf } from '../../../../helpers/common'
import usePermissions from '../../../../hooks/usePermissions'

import { getCampaign, clearCampaign } from '../../../../modules/actions/campaigns'
import {
  campaignSelector,
  campaignLoadingSelector,
  campaignWasLoadedSelector
} from '../../../../modules/selectors/campaigns'
import { selectedTikTokAdAccountSelector } from '../../../../modules/selectors/adAccounts'

import { CAMPAIGN_TIKTOK_EDIT } from '../../../../constants/forms'
import { NAME, BUDGET } from './fields'
import { TIKTOK_PLATFORM } from '../../../../constants/selectLists/platformList'
import { CAMPAIGN_BUDGET_SECTION } from '../../../../constants/permissions'

// here defined initial open states for function CampaignTikTokEdit sections
const sectionsInitialOpenStates = {
  [NAME]: false,
  [BUDGET]: false
}

function CampaignTikTokEdit({ shouldBeCleared }) {
  const dispatch = useDispatch()
  const permissions = usePermissions()

  const campaign = useSelector(campaignSelector)
  const campaignIsLoading = useSelector(campaignLoadingSelector)
  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)
  const { id: tiktokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)

  // to avoid loading representation
  const isInitialDataLoading = campaignIsLoading || !campaignWasLoaded

  const getItemDataHandler = useCallback(
    selectedEditItemId => {
      dispatch(getCampaign({ id: selectedEditItemId, account: tiktokAdAccountId }, TIKTOK_PLATFORM))
    },
    [dispatch, tiktokAdAccountId]
  )

  const clearItemDataHandler = useCallback(() => {
    if (shouldBeCleared) {
      dispatch(clearCampaign())
    }
  }, [dispatch, shouldBeCleared])

  useManageEditFormData({
    formName: CAMPAIGN_TIKTOK_EDIT,
    loadedDataId: campaign.id,
    getDataHandler: getItemDataHandler,
    clearDataHandler: clearItemDataHandler
  })

  // here defined all edit sections for CampaignTikTokEdit
  const editSections = useMemo(
    () => [
      {
        sectionName: NAME,
        title: 'Name',
        PreviewComponent: <NameSectionPreview />,
        FormComponent: <NameSectionForm />
      },
      ...insertIf(permissions.can('update', CAMPAIGN_BUDGET_SECTION), {
        sectionName: BUDGET,
        title: 'Budget',
        PreviewComponent: <TikTokCampaignBudgetSectionPreview campaign={campaign} />,
        FormComponent: <BudgetSection />
      })
    ],
    [campaign, permissions]
  )

  return (
    <FormDrawerWrapper title="Edit a TikTok campaign" formName={CAMPAIGN_TIKTOK_EDIT} showOpenButton={false}>
      <EditSectionsList
        editSections={editSections}
        isEditDataLoading={isInitialDataLoading}
        editItemData={campaign}
        sectionsInitialOpenStates={sectionsInitialOpenStates}
      />
    </FormDrawerWrapper>
  )
}

export default CampaignTikTokEdit
